import React, { useRef, useState, useEffect } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import BillingService from "../../../services/BillingService";
import Swal from "sweetalert2";

const styles = {
    container: { fontFamily: "Arial, sans-serif", fontSize: "13px", margin: "20px" },
    headingBox: {
        textAlign: "center",
        fontSize: "15px",
        fontWeight: "bold",
        marginBottom: "10px",
    },
    borderBox: {
        border: "1px solid #000",
        padding: "10px",
        marginBottom: "10px",
    },
    sectionRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
    },
    section: {
        border: "1px solid #000",
        padding: "6px",
        flex: 1,
        minWidth: "300px",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        textTransform: "uppercase",
    },
    thTd: {
        border: "1px solid #000",
        padding: "4px",
        verticalAlign: "top",
    },
    thead: {
        backgroundColor: "#f0f0f0",
    },
    signature: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: "40px",
        pageBreakInside: "avoid" // 🔥 FIX
    }
};

const PoPrintTemplate = ({ item }) => {
    const [billingAddress, setBillingAddress] = useState(null);
    const [supplier, setSupplier] = useState(null);
    const printRef = useRef();
    const numberToWordsWithPaise = (amount) => {
        const ones = [
            "",
            "One",
            "Two",
            "Three",
            "Four",
            "Five",
            "Six",
            "Seven",
            "Eight",
            "Nine",
            "Ten",
            "Eleven",
            "Twelve",
            "Thirteen",
            "Fourteen",
            "Fifteen",
            "Sixteen",
            "Seventeen",
            "Eighteen",
            "Nineteen"
        ];

        const tens = [
            "",
            "",
            "Twenty",
            "Thirty",
            "Forty",
            "Fifty",
            "Sixty",
            "Seventy",
            "Eighty",
            "Ninety"
        ];

        const numToWords = (number) => {
            if (number < 20) {
                return ones[number];
            }

            if (number < 100) {
                return `${tens[Math.floor(number / 10)]} ${ones[number % 10]
                    }`.trim();
            }

            if (number < 1000) {
                return `${ones[Math.floor(number / 100)]
                    } Hundred ${numToWords(
                        number % 100
                    )}`.trim();
            }

            if (number < 100000) {
                return `${numToWords(
                    Math.floor(number / 1000)
                )} Thousand ${numToWords(
                    number % 1000
                )}`.trim();
            }

            if (number < 10000000) {
                return `${numToWords(
                    Math.floor(number / 100000)
                )} Lakh ${numToWords(
                    number % 100000
                )}`.trim();
            }

            return `${numToWords(
                Math.floor(number / 10000000)
            )} Crore ${numToWords(
                number % 10000000
            )}`.trim();
        };

        const safeAmount =
            Number(amount) || 0;

        const [rupees, paise] =
            safeAmount
                .toFixed(2)
                .split(".")
                .map(Number);

        const rupeePart =
            rupees === 0
                ? ""
                : `${numToWords(rupees)} Rupees`;

        const paisePart =
            paise === 0
                ? ""
                : `${numToWords(paise)} Paise`;

        const combined = [
            rupeePart,
            paisePart
        ]
            .filter(Boolean)
            .join(" and ");

        return combined
            ? `${combined} Only`
            : "Zero Rupees Only";
    };

    // ✅ Fetch billing
    useEffect(() => {
        const fetchBillingAddress = async () => {
            try {
                const state = item?.requisitionNo?.store?.address?.state;
                if (state) {
                    const res = await BillingService.activeSuppliersState(state);
                    if (res.data?.length > 0) {
                        setBillingAddress(res.data[0]);
                    }
                }
            } catch (error) {
                Swal.fire("Error", "Billing fetch failed", "error");
            }
        };

        if (item) fetchBillingAddress();
    }, [item]);

    // ✅ Supplier
    useEffect(() => {
        if (item?.items?.length > 0) {
            setSupplier(item.items[0]?.supplier);
        }
    }, [item]);

    if (!item) return null;

    // 💰 Helpers
    const formatCurrency = (value) =>
        Number(value || 0).toLocaleString("en-IN", {
            style: "currency",
            currency: "INR",
        });

    const isFOC = item?.poType === "FOC PO";
    const hasIGST = item?.items?.some((row) => Number(row.igst) > 0);

    let totalQty = 0;
    let totalAmount = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalIGST = 0;
    let totalInclGst = 0;

    const getItemRow = (row, index) => {
        const qty = Number(row.approveQty) || 0;
        const rate = Number(row.rate) || 0;
        const cgst = Number(row.cgst) || 0;
        const sgst = Number(row.sgst) || 0;
        const igst = Number(row.igst) || 0;

        const base = qty * rate;
        const gst = base * ((cgst + sgst + igst) / 100);
        const total = base + gst;

        totalQty += qty;
        totalAmount += base;
        totalCGST += (cgst / 100) * base;
        totalSGST += (sgst / 100) * base;
        totalIGST += (igst / 100) * base;
        totalInclGst += total;

        return (
            <tr key={index}>
                <td style={styles.thTd}>{index + 1}</td>
                <td style={styles.thTd}>{row.description}</td>
                <td style={styles.thTd}>{row.unit}</td>
                <td style={styles.thTd}>{qty}</td>
                <td style={styles.thTd}>{formatCurrency(rate)}</td>
                {!isFOC && <td style={styles.thTd}>{formatCurrency(base)}</td>}
                {!isFOC && <td style={styles.thTd}>{cgst}</td>}
                {!isFOC && <td style={styles.thTd}>{sgst}</td>}
                {hasIGST && !isFOC && <td style={styles.thTd}>{igst}</td>}
                {!isFOC && <td style={styles.thTd}>{formatCurrency(total)}</td>}
            </tr>
        );
    };

    const directorApproval = item?.history
        ?.filter(h =>
            h?.action === "Approved" &&
            h?.toStatus === "Confirmed-Generated" // ✅ FINAL APPROVAL ONLY
        )
        ?.sort((a, b) => new Date(b.date) - new Date(a.date))[0];

    const formatDateTime = (date) => {
        if (!date) return "";

        const d = new Date(date);

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        const seconds = String(d.getSeconds()).padStart(2, "0");

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    const handlePrint = () => {
        if (!printRef.current) {
            return;
        }

        const printWindow = window.open(
            "",
            "",
            "height=900,width=1100"
        );

        if (!printWindow) {
            Swal.fire(
                "Print Blocked",
                "Please allow pop-ups to print the Purchase Order.",
                "warning"
            );

            return;
        }

        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
            <head>
                <title>
                    PO-${item?.poNumber || ""}
                </title>

                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />

                <style>
                    * {
                        box-sizing: border-box;
                    }

                    /*
                     * Do not force portrait or landscape.
                     * Browser print settings can now control
                     * the orientation.
                     */
                    @page {
                        margin: 8mm;
                    }

                    html,
                    body {
                        width: 100%;
                        margin: 0;
                        padding: 0;
                        background: #ffffff;
                        font-family: Arial, sans-serif;
                    }

                    body {
                        font-size: 11px;
                    }

                    table {
                        width: 100% !important;
                        min-width: 0 !important;
                        border-collapse: collapse !important;
                        table-layout: auto !important;
                        text-transform: uppercase;
                    }

                    th,
                    td {
                        border: 1px solid #000 !important;
                        padding: 4px !important;
                        vertical-align: top !important;
                        text-transform: uppercase;
                        word-break: normal !important;
                        overflow-wrap: break-word !important;
                    }

                    thead {
                        display: table-header-group;
                    }

                    tfoot {
                        display: table-footer-group;
                    }

                    tr {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }

                    img {
                        max-width: 180px !important;
                        height: auto !important;
                        display: block;
                    }

                    .scrolltable {
                        width: 100% !important;
                        max-width: 100% !important;
                        overflow: visible !important;
                    }

                    .signature {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }

                    h1,
                    h2,
                    h3,
                    h4,
                    h5,
                    h6,
                    p,
                    div,
                    span {
                        text-transform: uppercase;
                    }

                    ol {
                        margin: 0;
                        padding-left: 22px;
                    }

                    ol li {
                        text-transform: none;
                        page-break-inside: avoid;
                    }

                    @media print {
                        html,
                        body {
                            width: 100%;
                            margin: 0;
                            padding: 0;
                        }

                        body {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }

                        table {
                            width: 100% !important;
                            min-width: 0 !important;
                        }

                        .scrolltable {
                            overflow: visible !important;
                        }
                    }
                </style>
            </head>

            <body>
                ${printRef.current.innerHTML}
            </body>
        </html>
    `);

        printWindow.document.close();

        const images = Array.from(
            printWindow.document.images || []
        );

        Promise.all(
            images.map((image) => {
                if (image.complete) {
                    return Promise.resolve();
                }

                return new Promise((resolve) => {
                    image.onload = resolve;
                    image.onerror = resolve;
                });
            })
        ).then(() => {
            printWindow.focus();

            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 300);
        });
    };
    const handleDownloadPDF = async () => {
        const element = printRef.current;

        const canvas = await html2canvas(element, {
            scale: 1.5,              // 🔥 reduce size → faster + better alignment
            useCORS: true,
            backgroundColor: "#fff"
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.9);

        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = 210;
        const pageHeight = 297;

        const imgWidth = pageWidth - 10; // 🔥 margin fix
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let position = 0;

        // 🔥 First page
        pdf.addImage(imgData, "JPEG", 5, position, imgWidth, imgHeight);

        // 🔥 Add new pages properly
        let heightLeft = imgHeight - pageHeight;

        while (heightLeft > 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, "JPEG", 5, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        pdf.save(`PO-${item?.poNumber}.pdf`);
    };

    const finalApproval = item?.history
        ?.filter(h => h?.action === "Approved")
        ?.sort((a, b) => new Date(b.date) - new Date(a.date))[0];

    // ==========================================
    // PRINT ONLY CURRENT ACTIVE PO
    // ==========================================
    const userRole = localStorage.getItem("userRole");

    const restrictedPrintRoles = [
        "storeKeeper",
        "admin",
        "superAdmin",
        "billing"
    ];

    const canPrintCurrentPO =
        (
            !item?.printVersion ||
            item?.printVersion?.isCurrent === true
        ) &&
        !restrictedPrintRoles.includes(userRole);
    return (
        <>
            {/* 🔥 TOP ACTION BAR (LIKE GOOGLE DOCS) */}
            <div style={{
                position: "sticky",
                top: 0,
                zIndex: 999,
                padding: "10px",
                borderBottom: "1px solid #ddd",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px"
            }}>

                {/* PRINT - CURRENT ACTIVE PO ONLY */}
                {canPrintCurrentPO && (
                    <button
                        onClick={handlePrint}
                        style={{
                            padding: "6px 14px",
                            border: "1px solid #ccc",
                            background: "#f8f9fa",
                            cursor: "pointer",
                            borderRadius: "4px"
                        }}
                    >
                        🖨 Print
                    </button>
                )}

                {/* DOWNLOAD PDF - TEMPORARILY HIDDEN */}
                {/*
<button
    onClick={handleDownloadPDF}
    style={{
        padding: "6px 14px",
        border: "1px solid #0d6efd",
        background: "#0d6efd",
        color: "#fff",
        cursor: "pointer",
        borderRadius: "4px"
    }}
>
    ⬇ Download PDF
</button>
*/}
            </div>

            {/* ✅ ORIGINAL CONTENT */}
            <div
                ref={printRef}
                className="container-fluid p-3 p-md-4"
                style={{
                    ...styles.container,
                    width: "100%",
                    maxWidth: "none",
                    margin: 0,
                    boxSizing: "border-box",
                    overflow: "visible"
                }}
            >

                {/* HEADER */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "20px",
                        marginBottom: "25px"
                    }}
                >
                    {/* LEFT SIDE LOGO */}

                    <div
                        style={{
                            width: "35%",
                            textAlign: "left"
                        }}
                    >
                        <img
                            src="https://indigoinfra.in/img/product/product-1739471679615.jpeg"
                            alt="Indigo Logo"
                            crossOrigin="anonymous"
                            style={{
                                maxWidth: "200px",
                                width: "100%",
                                height: "auto",
                                display: "block"
                            }}
                        />
                    </div>

                    {/* RIGHT SIDE COMPANY DETAILS */}

                    <div
                        style={{
                            width: "65%",
                            textAlign: "right"
                        }}
                    >
                        <h2
                            style={{
                                margin: 0,
                                marginBottom: "18px",
                                fontSize: "28px",
                                fontWeight: "500"
                            }}
                        >
                            Indigo Infraprojects Pvt. Ltd.
                        </h2>

                        <div
                            style={{
                                fontSize: "13px",
                                lineHeight: "1.6",
                                textTransform: "uppercase"
                            }}
                        >
                            <div>
                                3rd Floor, Plot #12/B, Pocket 3,
                                Sec-17, Dwarka, New Delhi-110075
                            </div>

                            <div>
                                PH: +91-11-45720141, 44458664
                            </div>

                            <div>
                                INFO@INDIGOINFRAPROJECTS.COM
                            </div>

                            <div
                                style={{
                                    fontWeight: "bold"
                                }}
                            >
                                CIN: U70102DL2012PTC232657
                            </div>
                        </div>
                    </div>
                </div>

                {/* TITLE */}
                <div
                    style={styles.headingBox}
                    className="mt-3"
                >
                    {item?.status === "Confirmed-Generated"
                        ? "PURCHASE ORDER (DIGITAL COPY)"
                        : item?.status === "Order-Received"
                            ? "PURCHASE ORDER (ORDER RECEIVED)"
                            : item?.status === "Partially-Received"
                                ? "PURCHASE ORDER (PARTIALLY RECEIVED)"
                                : item?.status === "Completed"
                                    ? "PURCHASE ORDER (COMPLETED)"
                                    : item?.status === "Bill-Generated"
                                        ? "PURCHASE ORDER (UNDER BILLING)"
                                        : item?.status === "PaymentPending"
                                            ? "PURCHASE ORDER (PAYMENT PENDING)"
                                            : "PURCHASE ORDER (UNDER APPROVAL)"}
                </div>

                {/* DETAILS */}
                <div style={styles.borderBox}>
                    <div style={styles.sectionRow}>
                        <div style={styles.section}>
                            <div
                                style={{
                                    textTransform: "uppercase"
                                }}
                            >
                                <strong>PO No</strong>
                                {" - "}

                                {item?.createdAt
                                    ? new Date(
                                        item.createdAt
                                    ).getFullYear()
                                    : "-"}

                                {"/"}

                                {item?.createdAt
                                    ? String(
                                        new Date(
                                            item.createdAt
                                        ).getMonth() + 1
                                    ).padStart(2, "0")
                                    : "-"}

                                {"/"}

                                {item?.requisitionNo
                                    ?.store?.name ||
                                    "-"}

                                {"/"}

                                {item?.poNumber || "-"}

                                {Number(item?.printVersion?.revisionNo || 0) > 0 && (
                                    <div
                                        style={{
                                            marginTop: "3px"
                                        }}
                                    >
                                        <strong>
                                            Revision No.:
                                        </strong>{" "}
                                        {item?.printVersion?.revisionNo}

                                        {" | "}

                                        <strong>
                                            Revision Date:
                                        </strong>{" "}

                                        {item?.printVersion?.createdAt
                                            ? new Date(
                                                item.printVersion.createdAt
                                            ).toLocaleDateString("en-IN")
                                            : "-"}
                                    </div>
                                )}
                            </div>

                            <div>
                                <strong>
                                    Order Date:
                                </strong>{" "}

                                {item?.createdAt
                                    ? new Date(
                                        item.createdAt
                                    ).toLocaleDateString(
                                        "en-IN"
                                    )
                                    : "-"}
                            </div>

                            <div>
                                <strong>
                                    Requisition No:
                                </strong>{" "}

                                {item?.requisitionNo
                                    ?.requisitionNo ||
                                    "-"}
                            </div>
                        </div>

                        <div style={styles.section}>
                            <strong>Billing Address</strong><br />
                            Indigo Infraprojects Pvt. Ltd.<br />
                            {billingAddress?.address || "-"}<br />
                            <strong>GSTIN:</strong> {billingAddress?.gstNumber || "-"}
                        </div>
                    </div>

                    <div style={{ ...styles.sectionRow, marginTop: "5px" }}>
                        <div style={styles.section}>
                            <strong>Supplier Details</strong><br />
                            {supplier?.name || "-"}<br />
                            {supplier?.address?.street || "-"},
                            {supplier?.address?.city || "-"},
                            {supplier?.address?.state || "-"} -
                            {supplier?.address?.pincode || "-"},
                            {supplier?.address?.country || "-"}<br />

                            <strong>Kind Attn:</strong> {supplier?.contactPerson || "-"}<br />
                            <strong>Phone:</strong> {Array.isArray(supplier?.mobileNo)
                                ? supplier.mobileNo.join(", ")
                                : supplier?.mobileNo || "-"}<br />

                            <strong>GST:</strong> {supplier?.GSTNo || "-"}<br />
                            <strong>Email:</strong> {Array.isArray(supplier?.email)
                                ? supplier.email.join(", ")
                                : supplier?.email || "-"}
                        </div>

                        <div style={styles.section}>
                            <strong>Shipping Address</strong><br />
                            {item?.requisitionNo?.store?.name || "-"},
                            {item?.requisitionNo?.store?.address?.street || "-"}<br />
                            {item?.requisitionNo?.store?.address?.city || "-"},
                            {item?.requisitionNo?.store?.address?.state || "-"} -
                            {item?.requisitionNo?.store?.address?.pincode || "-"}<br />
                            {item?.requisitionNo?.store?.address?.country || "-"}<br />

                            <strong>Contact Person:</strong> {item?.requisitionNo?.user?.name || "-"}<br />
                            <strong>Phone:</strong> {item?.requisitionNo?.user?.mobileNo || "-"}
                        </div>
                    </div>
                </div>

                {/* SUBJECT */}
                <div style={{ marginBottom: "10px" }}>
                    <strong>Sub:</strong> {item?.subject || "-"} <br />
                    <strong>Ref:</strong> {item?.reference || "-"}
                </div>

                {/* TABLE */}
                <div
                    className="scrolltable"
                    style={{
                        width: "100%",
                        overflowX: "auto"
                    }}
                >
                    <table
                        style={{
                            ...styles.table,
                            width: "100%",
                            minWidth: 0,
                            tableLayout: "auto",
                            pageBreakInside: "auto"
                        }}
                    >
                        <thead className="sticky-table-header">
                            <tr style={{ pageBreakInside: "avoid" }}>
                                <th style={styles.thTd}>S. No.</th>
                                <th style={styles.thTd}>Description</th>
                                <th style={styles.thTd}>Unit</th>
                                <th style={styles.thTd}>Qty.</th>
                                <th style={styles.thTd}>Rate</th>
                                {!isFOC && <th style={styles.thTd}>Amount</th>}
                                {!isFOC && <th style={styles.thTd}>CGST (%)</th>}
                                {!isFOC && <th style={styles.thTd}>SGST (%)</th>}
                                {hasIGST && !isFOC && <th style={styles.thTd}>IGST (%)</th>}
                                {!isFOC && <th style={styles.thTd}>Amount incl. GST</th>}
                            </tr>
                        </thead>

                        <tbody>
                            {item?.items?.map((row, index) => {
                                const qty = Number(row.approveQty) || 0;
                                const rate = Number(row.rate) || 0;
                                const cgst = Number(row.cgst) || 0;
                                const sgst = Number(row.sgst) || 0;
                                const igst = Number(row.igst) || 0;

                                const baseAmount = qty * rate;
                                const gstAmount = baseAmount * ((cgst + sgst + igst) / 100);
                                const totalAmountRow = baseAmount + gstAmount;

                                totalQty += qty;
                                if (!isFOC) {
                                    totalAmount += baseAmount;
                                    totalCGST += (cgst / 100) * baseAmount;
                                    totalSGST += (sgst / 100) * baseAmount;
                                    totalIGST += (igst / 100) * baseAmount;
                                    totalInclGst += totalAmountRow;
                                }

                                return (
                                    <tr key={index}>
                                        <td style={styles.thTd}>{index + 1}</td>
                                        <td style={styles.thTd}>{row.description}</td>
                                        <td style={styles.thTd}>{row.unit}</td>
                                        <td style={styles.thTd}>{qty}</td>
                                        <td style={styles.thTd}>{formatCurrency(rate)}</td>

                                        {!isFOC && <td style={styles.thTd}>{formatCurrency(baseAmount)}</td>}
                                        {!isFOC && <td style={styles.thTd}>{cgst}</td>}
                                        {!isFOC && <td style={styles.thTd}>{sgst}</td>}
                                        {hasIGST && !isFOC && <td style={styles.thTd}>{igst}</td>}
                                        {!isFOC && <td style={styles.thTd}>{formatCurrency(totalAmountRow)}</td>}
                                    </tr>
                                );
                            })}

                            {/* ✅ TOTAL QTY */}
                            <tr>
                                <td colSpan="3" style={styles.thTd}><strong>Total Qty</strong></td>
                                <td style={styles.thTd}><strong>{totalQty}</strong></td>

                                {!isFOC && (
                                    <>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        {hasIGST && <td></td>}
                                        <td></td>
                                    </>
                                )}
                            </tr>

                            {/* ✅ TOTALS */}
                            {!isFOC && (
                                <>
                                    <tr>
                                        <td colSpan={hasIGST ? "9" : "8"} style={styles.thTd}>
                                            <strong>Total Amount</strong>
                                        </td>
                                        <td style={styles.thTd}>{formatCurrency(totalAmount)}</td>
                                    </tr>

                                    <tr>
                                        <td colSpan={hasIGST ? "9" : "8"} style={styles.thTd}>
                                            CGST Total
                                        </td>
                                        <td style={styles.thTd}>{formatCurrency(totalCGST)}</td>
                                    </tr>

                                    <tr>
                                        <td colSpan={hasIGST ? "9" : "8"} style={styles.thTd}>
                                            SGST Total
                                        </td>
                                        <td style={styles.thTd}>{formatCurrency(totalSGST)}</td>
                                    </tr>

                                    {hasIGST && (
                                        <tr>
                                            <td colSpan="9" style={styles.thTd}>
                                                IGST Total
                                            </td>
                                            <td style={styles.thTd}>{formatCurrency(totalIGST)}</td>
                                        </tr>
                                    )}

                                    <tr>
                                        <td colSpan={hasIGST ? "9" : "8"} style={styles.thTd}>
                                            <strong>Grand Total (incl. taxes & Rounded Off)</strong>
                                        </td>
                                        <td style={styles.thTd}>
                                            <strong>
                                                {Math.round(totalInclGst).toLocaleString("en-IN")}
                                            </strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td
                                            colSpan={hasIGST ? "10" : "9"}
                                            style={styles.thTd}
                                        >
                                            <strong>
                                                Grand Total (in words):
                                            </strong>{" "}

                                            {numberToWordsWithPaise(
                                                Math.round(totalInclGst)
                                            )}
                                        </td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* TERMS & CONDITIONS */}
                {Array.isArray(item?.termCondition) &&
                    item.termCondition.length > 0 && (

                        <div
                            style={{
                                marginTop: "20px",
                                pageBreakInside: "avoid"
                            }}
                        >
                            <p
                                style={{
                                    margin: "0 0 8px 0",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    textTransform: "uppercase"
                                }}
                            >
                                Terms & Conditions:
                            </p>

                            <ol
                                style={{
                                    margin: 0,
                                    paddingLeft: "25px",
                                    textAlign: "left"
                                }}
                            >
                                {item.termCondition.map(
                                    (term, index) => {

                                        const cleanTerm =
                                            String(term || "")
                                                .replace(
                                                    /^\d+\.\s*/,
                                                    ""
                                                );

                                        return (
                                            <li
                                                key={index}
                                                style={{
                                                    marginBottom: "4px"
                                                }}
                                            >
                                                {cleanTerm}
                                            </li>
                                        );
                                    }
                                )}
                            </ol>
                        </div>
                    )}

                {/* ✅ SIGNATURE (MATCHING ORIGINAL PO STRUCTURE) */}
                <div style={{ marginTop: "30px" }}>

                    {/* 🔥 NOTICE LINE */}
                    <div style={{ marginBottom: "15px", textAlign: "center" }}>
                        <strong>
                            Please sign and return back a copy of this Purchase Order as an acceptance.
                        </strong>
                    </div>

                    {/* 🔥 TWO COLUMN SIGNATURE */}
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "30px"
                    }}>

                        {/* LEFT SIDE (COMPANY) */}
                        {/* LEFT SIDE (COMPANY) */}
                        <div>
                            <p>
                                For and on behalf of<br />
                                <strong>Indigo Infraprojects Pvt. Ltd.</strong>
                            </p>

                            <div
                                style={{
                                    width: "150px",
                                    height: "80px",
                                    margin: "15px 0"
                                }}
                            />

                            {/* ✅ AUTHORISED SIGNATURE */}
                            <div style={{ textAlign: "left" }}>

                                {/* LABEL */}
                                <div>
                                    <strong>Authorised Signature</strong>
                                </div>

                                {/* SPACE FOR SIGN */}
                                <div
                                    style={{
                                        width: "150px",
                                        height: "60px",
                                        margin: "10px 0"
                                    }}
                                />

                            </div>
                        </div>

                        {/* RIGHT SIDE (SUPPLIER) */}
                        <div style={{ textAlign: "right" }}>
                            <p>
                                Received and Accepted By<br />
                                For and on behalf of<br />
                                <strong>{supplier?.name || "-"}</strong>
                            </p>

                            {/* SPACE FOR SIGN */}
                            <div style={{
                                width: "150px",
                                height: "80px",
                                margin: "15px 0",
                                marginLeft: "auto"
                            }} />

                            <span>Authorised Signature</span>
                        </div>

                    </div>
                </div>

                {/* CREATED BY */}

                <div
                    style={{
                        marginTop: "20px",
                        fontWeight: "bold",
                        fontSize: "10px",
                        textTransform: "uppercase"
                    }}
                >
                    Created By: {
                        item?.createdBy?.name ||
                        "N/A"
                    }
                </div>


                {/* ==========================================
    FOOTER
========================================== */}

                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "25px",
                        paddingTop: "6px",
                        borderTop: "1px solid #777",
                        fontSize: "9px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        pageBreakInside: "avoid"
                    }}
                >

                    <span>
                        PO No:{" "}
                        {item?.poNumber || "-"}
                    </span>


                    <span>
                        Generated From <strong>Indigo Infraprojects Pvt. Ltd. </strong>
                        Store Management System
                    </span>

                </div>

            </div>
        </>
    );

};

export default PoPrintTemplate;